import React from 'react';

import { Button, Tooltip, Upload, UploadFile } from 'antd';
import type { UploadProps } from 'antd/es/upload';
import { NotificationInstance } from 'antd/es/notification/interface';
import { ShowUploadListInterface } from 'antd/es/upload/interface';

import { PaperClipOutlined } from '@ant-design/icons';

import IBaseComponentProps from '@components/base-component-props';

import { exception } from '@extensions/notification';

import { useAppDispatch } from '@store/hooks';
import { userLoaded } from '@store/actions';

import { DocumentType } from '@enums/document-type';
import { DocumentReferenceType } from '@enums/document-reference-type';

interface IUploadDocumentProps extends IBaseComponentProps {
    documentType: DocumentType;
    referenceId: string;
    referenceType: DocumentReferenceType;
    files: Array<UploadFile> | undefined;
    showUploadList?: boolean | ShowUploadListInterface;
    accept?: string;
    multiple?: boolean;
    api: NotificationInstance;

    onChange: (fileList: Array<UploadFile>) => void;
}

const UploadDocument = (props: IUploadDocumentProps) => {
    const { documentType, referenceId, referenceType, files, showUploadList, accept, multiple, api, onChange, serverFetch } = props;

    const Buffer = require('buffer/').Buffer;

    const d = useAppDispatch();

    const onUpload = async (file: Blob): Promise<string> => {
        const formData = new FormData();
        formData.append('formFile', file);

        return serverFetch(`documents/${documentType}/${referenceId}/${referenceType}/upload`, {
            method: 'POST',
            body: formData,
        })
            .then((data: any) => {
                return data;
            })
            .catch((ex: any) => {
                exception(api, 'Ошибка загрузки файла', ex, () => d(userLoaded(undefined)));
                return undefined;
            });
    };

    const onDownload = async (id: string): Promise<any> => {
        return serverFetch(`documents/${id}/download`, { method: 'GET' })
            .then((data: any) => {
                return data;
            })
            .catch((ex: any) => {
                exception(api, 'Ошибка загрузки файла', ex, () => d(userLoaded(undefined)));
                return undefined;
            });
    };

    const onRemove = async (id: string): Promise<string> => {
        return serverFetch(`documents/${id}`, { method: 'DELETE' })
            .then((data: any) => {
                return data;
            })
            .catch((ex: any) => {
                exception(api, 'Ошибка удаления файла', ex, () => d(userLoaded(undefined)));
                return undefined;
            });
    };

    const handleChange: UploadProps['onChange'] = async ({ file, fileList, event }) => {
        if (!file) return;

        if (file.status == 'uploading' && event === undefined) {
            const result = await onUpload(file.originFileObj as Blob);

            if (result) {
                file.status = 'done';
                file.uid = result;
            } else {
                file.status = 'error';
            }
        } else if (file.status == 'removed') {
            const result = await onRemove(file.uid);
            if (result) {
                file.status = 'done';
            } else {
                file.status = 'error';
            }
        }

        if (onChange) onChange(fileList);
    };

    const handlePreview = async (file: any) => {
        if (file) {
            if (!file.url && !file.preview) {
                const result = await onDownload(file.uid);

                file.originFileObj = result.fileContents;
                file.contentType = result.contentType;
                file.name = result.fileDownloadName;

                let fileObj = file.originFileObj;

                if (typeof fileObj !== 'object') {
                    const buffer = new Buffer.from(file.originFileObj, 'base64');
                    fileObj = new Blob([buffer], { type: file.contentType });
                }

                const fileURL = URL.createObjectURL(fileObj);
                window.open(fileURL);
            }
        }
    };

    return (
        <Upload
            onChange={(value) => {
                value.file.response = referenceId;

                if (handleChange) handleChange(value);
            }}
            onPreview={handlePreview}
            listType='text'
            multiple={multiple}
            fileList={files}
            showUploadList={showUploadList}
            accept={accept || '.pdf,.jpeg,.jpg,.png'}
        >
            <Tooltip title='Прикрепить документы'>
                <Button size='small' type='primary' icon={<PaperClipOutlined />}></Button>
            </Tooltip>
            {files && files?.length <= 0 && <span style={{ marginLeft: 10 }}>Прикрепить документы...</span>}
        </Upload>
    );
};

export default UploadDocument;
